import {
  ThemeDefinition,
  ThemeProvider,
  Breakpoints as VwBreakpoints,
} from '@volkswagen-onehub/components-core';
import {
  Breakpoints,
  EMobilityToolsFeatureAppTheme,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import React from 'react';
import styled, {ThemeContext} from 'styled-components';
import {isV26, ThemeWithVariant} from './design-variant';

interface BrandThemeProviderProps {
  theme: 'main' | 'inverted';
  children: React.ReactNode;
}

export const BrandThemeProvider = (props: BrandThemeProviderProps) => {
  return (
    <ThemeProvider theme={props.theme}>
      <ThemeContext.Consumer>
        {(vwTheme) => (
          <ThemeProvider theme={getThemeWithoutDeprecatedObjects(vwTheme)}>
            {props.children}
          </ThemeProvider>
        )}
      </ThemeContext.Consumer>
    </ThemeProvider>
  );
};

export const EMobilityFeatureAppWrapper = (props: {
  children?: React.ReactNode;
}): JSX.Element => <BreakpointsWrapper>{props.children}</BreakpointsWrapper>;

// don't import/spread the whole theme, since it holds deprecated keys, that shouldn't be used anymore
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getThemeWithoutDeprecatedObjects = (theme: any) => {
  return {
    animation: theme.animation,
    size: theme.size,
    fonts: theme.fonts,
    appearances: theme.appearances,
    colors: theme.colors,
  };
};

export function createEMobilityToolsFeatureAppVariantTheme(
  theme: ThemeWithVariant
) {
  return isV26(theme)
    ? createEMobilityToolsFeatureAppThemeV26(theme)
    : createEMobilityToolsFeatureAppTheme(theme);
}

// TODO: rename once fully migrated to v26
export function createEMobilityToolsFeatureAppThemeV26(
  theme: ThemeDefinition
): EMobilityToolsFeatureAppTheme {
  return {
    colors: {
      app: {
        background: theme.colors.background.primary,
      },
      inputArea: {
        background:
          'linear-gradient(167deg, rgba(255, 255, 255, 0.80) 0%, rgba(255, 255, 255, 0.20) 60%, rgba(255, 255, 255, 0.00) 100%)', //TODO: backgrounds-cardEmphasis ?
        shadow: theme.colors.shadow.default,
        drivingProfileSlider: {
          evenBackground: 'transparent',
          oddBackground: 'transparent',
          separator: theme.colors.border.default,
          text: theme.colors.content.active,
          thumb: {
            background: {
              default: theme.colors.controllers.on.default,
              hover: theme.colors.controllers.on.hover,
              focus: theme.colors.controllers.on.default,
            },
            border: {
              default: theme.colors.controllers.on.default,
              hover: theme.colors.controllers.on.hover,
              focus: theme.colors.focus.main,
            },
            text: {
              default: theme.colors.content.inverted,
              focus: theme.colors.content.inverted,
            },
          },
        },
        genericChoiceButton: {
          background: {
            default: 'transparent',
            selected: 'transparent',
          },
          border: {
            default: theme.colors.border.default,
            selected: theme.colors.filter.on.default,
            hover: theme.colors.filter.off.hover,
            focus: theme.colors.focus.main,
            disabled: theme.colors.border.disabled,
          },
          content: {
            default: theme.colors.content.primary,
            selected: theme.colors.content.primary,
            hover: theme.colors.content.primary,
            focus: theme.colors.content.primary,
            disabled: theme.colors.content.disabled,
          },
        },
      },
      resultArea: {
        background: theme.colors.content.primary,
        borderColor: theme.colors.border.default,
      },
    },
    elements: {
      modelSelectionArea: {
        header: {
          marginTop: theme.size.static0,
        },
        gap: theme.size.static100,
      },
      inputArea: {
        combustionPriceLayout: {
          gap: theme.size.static250,
        },
        genericChoiceButton: {
          border: {
            radius: {
              default: theme.size.borderRadius.cardS,
              focus: theme.size.borderRadius.cardS,
            },
            width: {
              default: '1px',
              selected: '2px',
            },
          },
          padding: {
            mobile: {
              withIcon: `7px ${theme.size.static200}`,
              withoutIcon: `${theme.size.static150} ${theme.size.static250}`,
            },
            desktop: {
              withIcon: theme.size.static250,
              withoutIcon: `${theme.size.static150} ${theme.size.static200}`,
            },
          },
          gap: theme.size.static200,
        },
        drivingProfileSlider: {
          borderRadius: theme.size.borderRadius.cardS,
        },
        borderRadius: {
          default: theme.size.borderRadius.cardL,
          secondary: theme.size.borderRadius.cardL,
        },
      },
      disclaimersArea: {
        marginTop: theme.size.dynamic0150,
      },
      efficiencyData: {
        separator: '\u00a0• ',
        marginTop: theme.size.static100,
      },
      resultArea: {
        borderRadius: theme.size.borderRadius.cardS,
      },
      configureButton: {
        marginTop: theme.size.static350,
      },
    },
    spacing: {
      appLayout: {
        blockGap: theme.size.grid001,
      },
      simulatorHeaderLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
      },
      oneColumnSimulatorContentLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
        blockGap: theme.size.static350,
      },
      simulatorLayout: {
        blockGap: theme.size.dynamic0130,
      },
      twoColumnSimulatorContentLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
        inlineGap: theme.size.grid001,
        blockGap: theme.size.static350,
        sectionColumnWidth: {
          carSelection: '2.45fr',
          inputs: '4.2fr',
        },
      },
      inputSectionLayout: {
        inlineStart: 'var(--input-section-layout-inline-start)',
        inlineEnd: 'var(--input-section-layout-inline-end)',
        blockStart: theme.size.dynamic0130,
        blockEnd: theme.size.dynamic0130,
      },
      faqLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
      },
      infoIconOffset: '-4px',
      inputSectionVerticalGap: 'var(--input-simulator-vertical-gap)',
      resultSectionLayout: {
        inlineStart: theme.size.dynamic0100,
        blockStart: theme.size.dynamic0100,
      },
      questionsMarginBottom: theme.size.static200,
    },
  };
}

const BreakpointsWrapper = styled.div`
  --input-section-layout-inline-start: ${(props) =>
    props.theme.size.dynamic0040};
  --input-section-layout-inline-end: ${(props) => props.theme.size.dynamic0040};
  --input-simulator-vertical-gap: ${(props) => props.theme.size.dynamic0200};

  @media (min-width: ${VwBreakpoints.b560}px) {
    --input-simulator-vertical-gap: ${(props) => props.theme.size.dynamic0130};
  }

  @media (min-width: ${Breakpoints.twoColumnLayout}px) {
    --input-section-layout-inline-start: ${(props) =>
      props.theme.size.dynamic0130};
    --input-section-layout-inline-end: ${(props) =>
      props.theme.size.dynamic0130};
  }
`;

// TODO: to be removed once fully migrated to v26
export function createEMobilityToolsFeatureAppTheme(
  theme: ThemeDefinition
): EMobilityToolsFeatureAppTheme {
  return {
    colors: {
      app: {
        background: theme.colors.background.primary,
      },
      inputArea: {
        border: {
          default: theme.colors.border.default,
        },
        drivingProfileSlider: {
          evenBackground: 'transparent',
          oddBackground: theme.colors.background.tertiary,
          separator: theme.colors.border.default,
          text: theme.colors.content.active,
          thumb: {
            background: {
              default: theme.colors.controllers.off.default,
              focus: theme.colors.controllers.on.default,
            },
            border: {
              default: theme.colors.controllers.off.default,
              focus: theme.colors.controllers.off.default,
            },
            text: {
              default: theme.colors.content.onsurface,
              focus: theme.colors.content.onsurface,
            },
          },
        },
        genericChoiceButton: {
          background: {
            default: 'transparent',
            selected: 'transparent',
          },
          border: {
            default: theme.colors.button.secondary.default,
            selected: theme.colors.button.secondary.active,
            hover: theme.colors.button.secondary.hover,
            focus: theme.colors.focus.main,
            disabled: theme.colors.button.secondary.disabled,
          },
        },
      },
      resultArea: {
        background: theme.colors.gradient.right,
        borderColor: theme.colors.border.default,
        stickyResult: {
          topBorder: theme.colors.border.divider,
        },
      },
    },
    elements: {
      modelSelectionArea: {
        header: {
          marginTop: theme.size.static0,
        },
        gap: theme.size.static100,
      },
      inputArea: {
        combustionPriceLayout: {
          gap: theme.size.static250,
        },
        genericChoiceButton: {
          border: {
            radius: {
              default: theme.size.borderRadius.cardS,
              focus: theme.size.borderRadius.cardS,
            },
            width: {
              default: '1px',
              selected: '2px',
            },
          },
          padding: {
            mobile: {
              withIcon: `7px ${theme.size.static200}`,
              withoutIcon: `${theme.size.static150} ${theme.size.static250}`,
            },
            desktop: {
              withIcon: theme.size.static250,
              withoutIcon: `${theme.size.static150} ${theme.size.static200}`,
            },
          },
          gap: theme.size.static200,
        },
        drivingProfileSlider: {
          borderRadius: theme.size.borderRadius.cardS,
        },
        borderRadius: {
          default: theme.size.borderRadius.cardL,
          secondary: theme.size.borderRadius.cardL,
        },
      },
      disclaimersArea: {
        marginTop: theme.size.dynamic0150,
      },
      efficiencyData: {
        separator: '\u00a0• ',
        marginTop: theme.size.static100,
      },
      resultArea: {
        borderRadius: theme.size.borderRadius.cardS,
      },
      configureButton: {
        marginTop: theme.size.static350,
      },
    },
    spacing: {
      appLayout: {
        blockGap: theme.size.grid001,
      },
      simulatorHeaderLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
      },
      oneColumnSimulatorContentLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
        blockGap: theme.size.static300,
      },
      simulatorLayout: {
        blockGap: theme.size.dynamic0130,
      },
      twoColumnSimulatorContentLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
        inlineGap: theme.size.grid001,
        blockGap: theme.size.static350,
        sectionColumnWidth: {
          carSelection: '2.45fr',
          inputs: '4.2fr',
        },
      },
      inputSectionLayout: {
        inlineStart: 'var(--input-section-layout-inline-start)',
        inlineEnd: 'var(--input-section-layout-inline-end)',
        blockStart: theme.size.dynamic0130,
        blockEnd: theme.size.dynamic0130,
      },
      faqLayout: {
        inlineStart: theme.size.grid002,
        inlineEnd: theme.size.grid002,
      },
      infoIconOffset: '-4px',
      inputSectionVerticalGap: 'var(--input-simulator-vertical-gap)',
      resultSectionLayout: {
        inlineStart: theme.size.dynamic0100,
        blockStart: theme.size.dynamic0100,
      },
      questionsMarginBottom: theme.size.static200,
    },
  };
}
